import React, { useMemo } from 'react';

import { Actions, Action } from '@gogame/components';
import { useScreenGo, useScreenState } from '@gogame/screens';
import { useGridApiContext } from '@gogame/data-grid';

import { BulkEditConceptsForm } from 'scenes/Gallery/GalleryPage/Concepts/BulkEditForm';
import { CreateConceptFamilyForm } from 'scenes/Gallery/GalleryPage/ConceptFamilies/ConceptFamilyForm';
import * as CONCEPT_FAMILY from 'scenes/Gallery/GalleryPage/ConceptFamilies/constants';
import BatchAddToConceptFamilyForm from 'scenes/Gallery/GalleryPage/Concepts/BatchAddToConceptFamilyForm';
import {
  BatchShareConceptsForm,
  BatchUnshareConceptsForm,
} from 'scenes/Gallery/GalleryPage/Concepts/BatchShareForm';
import {
  useBatchArchive,
  useBatchUnarchive,
  useBatchRemove,
  useBatchMarkAsRead,
  useExportConcepts,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import {
  EXCEL,
  TAB_CONCEPT_FAMILIES,
  ZIP,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import {
  THUMBNAIL_URL,
  VIDEO_LENGTH,
} from 'scenes/Gallery/components/File/constants';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';

const BatchToolbar = ({ selectedRows, openChildComponent }) => {
  const go = useScreenGo();
  const { filters } = useScreenState();
  const {
    isAllowArchiving,
    isAllowManageConceptFamilies,
    isAllowShare,
    isAllowBatchDelete,
    isAllowCreateConcept,
  } = useGalleryPermissions();
  const [batchDeleteConcepts] = useBatchRemove();
  const [batchArchiveConcepts] = useBatchArchive();
  const [batchUnarchiveConcepts] = useBatchUnarchive();
  const [batchMarkAsReadConcepts] = useBatchMarkAsRead();

  const [exportExcelConcepts] = useExportConcepts({
    type: EXCEL,
  });

  const [exportZipConcepts] = useExportConcepts({
    type: ZIP,
  });

  const handleExportZipConceptsSelectedFiltered = (selectedRows) => {
    const size = selectedRows.reduce(
      (result, { size = 0 }) => result + size,
      0
    );
    return exportZipConcepts(
      {
        ids: selectedRows.map(({ id }) => id),
        useInnerFilter: true,
        filters,
      },
      { size }
    );
  };

  const handleExportZipConceptsSelected = (selectedRows) => {
    const size = selectedRows.reduce(
      (result, { size = 0 }) => result + size,
      0
    );
    return exportZipConcepts(
      {
        ids: selectedRows.map(({ id }) => id),
      },
      { size }
    );
  };

  const handleExportExcelConceptsSelected = (selectedRows) =>
    exportExcelConcepts({
      ids: selectedRows.map(({ id }) => id),
    });

  const handleBatchArchive = (selectedRows) =>
    batchArchiveConcepts(selectedRows);
  const handleBatchUnarchive = (selectedRows) =>
    batchUnarchiveConcepts(selectedRows);
  const handleBatchDelete = (selectedRows) => batchDeleteConcepts(selectedRows);
  const handleBatchMarkAsRead = (selectedRows) =>
    batchMarkAsReadConcepts(selectedRows);

  const handleBulkEdit = (selectedRows) => {
    openChildComponent(BulkEditConceptsForm, {
      selectedRows,
    });
  };

  const handleCreateFamily = (selectedRows) => {
    openChildComponent(CreateConceptFamilyForm, {
      initialValues: {
        [CONCEPT_FAMILY.CONCEPTS]: selectedRows.map(({ id }) => id),
      },
      onSubmit: ({ id }) => {
        go(`../${TAB_CONCEPT_FAMILIES}/${id}`);
      },
    });
  };

  const handleAddToFamily = (selectedRows) => {
    openChildComponent(BatchAddToConceptFamilyForm, {
      selectedRows,
    });
  };

  const handleBatchShare = (selectedRows) => {
    openChildComponent(BatchShareConceptsForm, {
      selectedRows,
    });
  };

  const handleBatchUnShare = (selectedRows) => {
    openChildComponent(BatchUnshareConceptsForm, {
      selectedRows,
    });
  };

  return (
    <Actions limit={3}>
      <Action
        button
        onClick={() => handleBatchMarkAsRead(selectedRows)}
        /* eslint-disable-next-line */
        data-cy-meta={'Action'}
        data-cy-label={'Mark As Read'}
        label={'Mark As Read'}
      />
      <Action button label={'Download'}>
        <Action
          onClick={() => handleExportZipConceptsSelectedFiltered(selectedRows)}
          label={'Download with filtered variants only'}
        />
        <Action
          onClick={() => handleExportZipConceptsSelected(selectedRows)}
          label={'Download with all variants'}
        />
      </Action>
      {isAllowCreateConcept ? (
        <Action
          button
          label={'Bulk Edit'}
          onClick={() => handleBulkEdit(selectedRows)}
        />
      ) : null}
      <Action
        label={'Export to excel'}
        onClick={() => handleExportExcelConceptsSelected(selectedRows)}
      />
      {isAllowShare && (
        <Action
          label={'Share'}
          onClick={() => handleBatchShare(selectedRows)}
        />
      )}
      {isAllowShare && (
        <Action
          label={'Unshare'}
          onClick={() => handleBatchUnShare(selectedRows)}
        />
      )}
      {isAllowManageConceptFamilies && (
        <Action
          label={'Create Family'}
          onClick={() => handleCreateFamily(selectedRows)}
        />
      )}

      {isAllowManageConceptFamilies && (
        <Action
          label={'Add to Family'}
          onClick={() => handleAddToFamily(selectedRows)}
        />
      )}
      {isAllowArchiving && (
        <Action
          label={'Archive'}
          onClick={() => handleBatchArchive(selectedRows)}
        />
      )}
      {isAllowArchiving && (
        <Action
          label={'Activate'}
          onClick={() => handleBatchUnarchive(selectedRows)}
        />
      )}
      {isAllowBatchDelete && (
        <Action
          label={'Delete'}
          onClick={() => handleBatchDelete(selectedRows)}
        />
      )}
    </Actions>
  );
};

export default BatchToolbar;

export const BatchToolbarDataGrid = ({ selectedValues, ...props }) => {
  const {
    current: { getRow },
  } = useGridApiContext();

  const selectedRows = useMemo(() => {
    if (!getRow) {
      return selectedValues.map((id) => ({ id }));
    }

    return selectedValues.map((id) => {
      const {
        [CONCEPT.NAME]: name,
        [CONCEPT.ORIGINAL]: {
          [CREATIVE.FILE]: {
            [THUMBNAIL_URL]: originalFileThumbnailURL,
            [VIDEO_LENGTH]: originalFileVideoLength,
          },
        },
        [CONCEPT.IS_THUMBNAIL_GENERATING]: isThumbnailGenerating,
      } = getRow(id);

      return {
        id,
        name,
        src: originalFileThumbnailURL,
        videoLength: originalFileVideoLength,
        thumbnail: isThumbnailGenerating,
      };
    });
  }, [selectedValues, getRow]);

  return <BatchToolbar selectedRows={selectedRows} {...props} />;
};
